import { Switch } from "./ui/switch";
import { formatCurrency } from "../lib/utils";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

function formatSummaryCurrency(value) {
  return formatCurrency(value, "BRL", "pt-BR", 2);
}

function getSaldoVisual(saldo) {
  if (saldo > 0) {
    return {
      saldoColor: "text-success",
      Icon: TrendingUp,
    };
  }

  if (saldo < 0) {
    return {
      saldoColor: "text-destructive",
      Icon: TrendingDown,
    };
  }

  return {
    saldoColor: "text-foreground",
    Icon: Minus,
  };
}

function resolveSummarySaldo(income, expense, balance) {
  if (balance === null || balance === undefined) return income - expense;

  const numericBalance = Number(balance);
  return Number.isFinite(numericBalance) ? numericBalance : income - expense;
}

function SummaryRow({ label, income, expense, balance, compact = false }) {
  const saldo = resolveSummarySaldo(income, expense, balance);
  const { saldoColor, Icon } = getSaldoVisual(saldo);

  return (
    <div className={`grid min-w-0 grid-cols-[40px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)] items-center gap-1 ${compact ? "text-[9px]" : "text-[10px] sm:text-sm"}`}>
      <span className="truncate text-[8px] font-bold uppercase leading-none tracking-normal text-muted-foreground sm:text-xs">
        {label}
      </span>
      <div className="min-w-0">
        <span className="hidden text-muted-foreground sm:block">Receitas</span>
        <span className="block truncate whitespace-nowrap font-bold leading-none tabular-nums sm:leading-normal">{formatSummaryCurrency(income)}</span>
      </div>
      <div className="min-w-0">
        <span className="hidden text-muted-foreground sm:block">Despesas</span>
        <span className="block truncate whitespace-nowrap font-bold leading-none tabular-nums sm:leading-normal">{formatSummaryCurrency(expense)}</span>
      </div>
      <div className="min-w-0">
        <span className="hidden text-muted-foreground sm:block">Saldo</span>
        <div className={`flex min-w-0 items-center gap-0.5 font-bold leading-none tabular-nums sm:leading-normal ${saldoColor}`}>
          <Icon className="h-2.5 w-2.5 shrink-0 sm:h-4 sm:w-4" />
          <span className="truncate whitespace-nowrap">{formatSummaryCurrency(saldo)}</span>
        </div>
      </div>
    </div>
  );
}

function SummaryValue({ value, color = "text-foreground", icon: Icon = null }) {
  return (
    <div className={`flex min-w-0 items-center gap-1 text-xs font-bold tabular-nums lg:text-sm ${color}`}>
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0 lg:h-4 lg:w-4" />}
      <span className="truncate whitespace-nowrap">{formatSummaryCurrency(value)}</span>
    </div>
  );
}

export function MonthSaldoBar({
  income,
  expense,
  projectedIncome = income,
  projectedExpense = expense,
  projectedBalance,
  realizedIncome = income,
  realizedExpense = expense,
  realizedBalance,
  realizadoOnly,
  onToggle,
}) {
  const projectedSaldo = resolveSummarySaldo(projectedIncome, projectedExpense, projectedBalance);
  const realizedSaldo = resolveSummarySaldo(realizedIncome, realizedExpense, realizedBalance);
  const saldo = realizadoOnly ? realizedSaldo : projectedSaldo;
  const projectedVisual = getSaldoVisual(projectedSaldo);
  const realizedVisual = getSaldoVisual(realizedSaldo);
  
  // Decide styling directly based on saldo
  let barTheme = "bg-muted/40 border-transparent";

  if (saldo > 0) {
    barTheme = "bg-success/10 border-success/20";
  } else if (saldo < 0) {
    barTheme = "bg-destructive/10 border-destructive/20";
  }

  return (
    <div className={`rounded-lg border p-2 transition-colors sm:p-4 ${barTheme}`}>
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 sm:hidden">
        <div className="min-w-0 space-y-1">
          <div className="grid min-w-0 grid-cols-[40px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)] gap-1 text-[8px] font-semibold uppercase leading-none tracking-normal text-muted-foreground sm:text-sm sm:normal-case sm:leading-normal">
            <span />
            <span>Receitas</span>
            <span>Despesas</span>
            <span>Saldo</span>
          </div>
          <SummaryRow label="Projetado" income={projectedIncome} expense={projectedExpense} balance={projectedBalance} compact />
          <SummaryRow label="Realizado" income={realizedIncome} expense={realizedExpense} balance={realizedBalance} compact />
        </div>

        <div className="flex shrink-0 flex-col items-center gap-1 border-l border-border/50 pl-1.5 sm:pl-2">
          <span className="text-center text-[8px] font-bold uppercase leading-none tracking-normal text-muted-foreground sm:text-sm sm:normal-case sm:leading-normal">
            {realizadoOnly ? "Conciliado" : "Todos"}
          </span>
          <Switch
            checked={realizadoOnly}
            onCheckedChange={onToggle}
            aria-label="Alternar entre todos e conciliados"
          />
        </div>
      </div>

      <div className="hidden grid-cols-[minmax(0,1fr)_auto] items-center gap-5 sm:grid">
        <div className="grid min-w-0 grid-cols-[86px_repeat(3,minmax(0,1fr))] items-center gap-x-6 gap-y-2 lg:grid-cols-[96px_repeat(3,minmax(0,1fr))] lg:gap-x-8">
          <span />
          <span className="text-xs font-semibold text-muted-foreground lg:text-sm">Receitas</span>
          <span className="text-xs font-semibold text-muted-foreground lg:text-sm">Despesas</span>
          <span className="text-xs font-semibold text-muted-foreground lg:text-sm">Saldo</span>

          <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground lg:text-xs">Projetado</span>
          <SummaryValue value={projectedIncome} />
          <SummaryValue value={projectedExpense} />
          <SummaryValue value={projectedSaldo} color={projectedVisual.saldoColor} icon={projectedVisual.Icon} />

          <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground lg:text-xs">Realizado</span>
          <SummaryValue value={realizedIncome} />
          <SummaryValue value={realizedExpense} />
          <SummaryValue value={realizedSaldo} color={realizedVisual.saldoColor} icon={realizedVisual.Icon} />
        </div>

        <div className="flex shrink-0 items-center gap-2 border-l border-border/50 pl-5">
          <span className="text-sm font-semibold text-muted-foreground">
            {realizadoOnly ? "Conciliado" : "Todos"}
          </span>
          <Switch
            checked={realizadoOnly}
            onCheckedChange={onToggle}
            aria-label="Alternar entre todos e conciliados"
          />
        </div>
      </div>
    </div>
  );
}
